import askQuestionLocales from './generated/ask-question-locales.json';
import { normalizeLocale, type SupportedLocale } from './config';
import {
  getCameraCaptureCopy,
  type CameraCaptureCopy,
} from './camera-capture-copy';

export type AskQuestionCopy = {
  yes: string;
  no: string;
  customAnswer: string;
  customPlaceholder: string;
  chooseYesNo: string;
  chooseYesNoOrType: string;
  send: string;
  sending: string;
  answered: string;
  alreadyHandled: string;
  /** A card the user's next chat message resolved. */
  answeredInChat: string;
  noLongerAwaiting: string;
  /** A newer question from the agent replaced this unanswered card. */
  replacedByNewerQuestion: string;
  /** The user declined this approval in a chat message (#6136). */
  declined: string;
  /** Card footer for a share reader, who cannot answer (#5814). */
  waitingForAnswer: string;
  /** "Waiting for {name}": the person whose message led to the card (#5814). */
  waitingForPerson: string;
  /** "Answered by {name}" in a task several people use (#5814). */
  answeredByPerson: string;
  /** A member who may only view the task tried to answer (#5814). */
  viewOnlyCannotAnswer: string;
  clear: string;
  back: string;
  thinking: string;
  submit: string;
  required: string;
  optional: string;
  working: string;
  proceed: string;
  questionCountOne: string;
  questionCountOther: string;
  questionsTitle: string;
  numberType: string;
  enterNumber: string;
  increaseValue: string;
  decreaseValue: string;
  imageUnavailable: string;
  ratingType: string;
  attachNamed: string;
  attachFiles: string;
  tooManyFiles: string;
  answeredProgress: string;
  notAnswered: string;
  typeCustomAnswer: string;
  addAnotherAnswer: string;
  attachReference: string;
  chooseHumanSubject: string;
  multiSelect: string;
  yesNo: string;
  text: string;
  singleChoice: string;
  confirmationPhrase: string;
  destructiveConfirmationHelp: string;
  allowSimilarActions: string;
  dontRun: string;
  approveAndRun: string;
  approveCountAndRun: string;
  custom: string;
  scaleValue: string;
  scaleRange: string;
  emptySocialFields: string;
  answerSubmissionFailed: string;
  destructive: string;
  review: string;
  allowAction: string;
  allActionsAllowed: string;
  actionsAllowedProgress: string;
  approvedSummary: string;
  alsoAllowSimilar: string;
  doNotRunAnswer: string;
  chooseOneOrMoreOrType: string;
  deepResearchContext: string;
  enterDetails: string;
  referencesUnavailable: string;
  unsupportedFilesSkipped: string;
  uploadFailed: string;
  removeAttachment: string;
  answerRequired: string;
  answerSavedAgentPaused: string;
  connectionInterrupted: string;
  checkingSavedAnswer: string;
  unspecified: string;
  next: string;
  /** "Answer {questions} to continue." with the names listed. */
  answerQuestionsToContinue: string;
  /** Locale quotes around one question name: "“{question}”". */
  quotedQuestionLabel: string;
  openArtifact: string;
  noMatches: string;
  productCategoryGuidance: string;
  requiredQuestionNeedsAnswer: string;
  showingMatches: string;
  searchPresets: string;
  search: string;
  usePresetsInstead: string;
  useSuggestedCategories: string;
  noSuggestedCategories: string;
  presetReferencesAvailable: string;
  customCategory: string;
  noProductPresetsAvailable: string;
  presetReferencesNotSetup: string;
  startResearch: string;
  gtbsConfirmTitleGeneric: string;
  gtbsRepinTitleGeneric: string;
  gtbsHelperText: string;
  gtbsAffirmative: string;
  gtbsNegative: string;
  gtbsFooterPrompt: string;
  gtbsSubmit: string;
  gtbsReference: string;
  technicalDetails: string;
  blueprint: string;
  category: string;
  construction: string;
  noTraitsSelected: string;
  voiceQuestionNotReady: string;
  voiceQuestionnaireNotReady: string;
  voiceNoPreviousSet: string;
  voiceMovedPreviousSilent: string;
  voiceConfirmSubmit: string;
  voiceConfirmNext: string;
  voiceStayOnSet: string;
  voiceMovedNextSilent: string;
  voiceRequiredCannotSkip: string;
  voiceAskMissingDetails: string;
  voiceOptionalUnspecifiedSilent: string;
  voiceNoAnswerCaught: string;
  voiceReturnedIncompleteSilent: string;
  voiceAnswersAppliedSilent: string;
  voiceSubmittedContinuingSilent: string;
  voiceSavedWithoutResume: string;
  voiceQuestionnaireSubmitFailed: string;
  voiceRequiredAnswerAgain: string;
  voiceMissingAnswerAgain: string;
  voiceInvalidOption: string;
  voiceNoPendingQuestion: string;
  voiceRegisteredFormRequiresChat: string;
  voiceAnswerResumedSilent: string;
  voiceAnswerSavedSilent: string;
  voiceConnectionInterruptedSilent: string;
  voiceAnswerFailed: string;
  brandSourceReviewTitle: string;
  brandSourceConfirmation: string;
  brandSourceHandlePlaceholder: string;
  brandSourceWebsite: string;
} & CameraCaptureCopy;

type AskQuestionLocaleCopy = Omit<AskQuestionCopy, keyof CameraCaptureCopy> & {
  resourceLabels?: { printsAndPatterns?: string };
};

function getLocaleCopy(locale: string): AskQuestionLocaleCopy {
  return askQuestionLocales[
    normalizeLocale(locale) as SupportedLocale
  ] as AskQuestionLocaleCopy;
}

export function getAskQuestionCopy(locale: string): AskQuestionCopy {
  return {
    ...getLocaleCopy(locale),
    ...getCameraCaptureCopy(locale),
  };
}

export function formatAskQuestionCopy(
  template: string,
  values: Record<string, string | number>
): string {
  return Object.entries(values).reduce(
    (message, [key, value]) => message.replaceAll(`{${key}}`, String(value)),
    template
  );
}

export function localizeAskQuestionResourceLabel(
  locale: string,
  label: string
): string {
  if (label.trim().toLowerCase() !== 'prints & patterns') return label;
  return getLocaleCopy(locale).resourceLabels?.printsAndPatterns ?? label;
}
